import { useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  MessageSquarePlus,
  Settings,
  User,
  type LucideIcon,
} from "lucide-react";
import { cn, initials } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { getIcon } from "@/lib/iconMap";
import { useNavModules } from "@/modules/module-manager/hooks/useModules";
import type { ModuleConfig } from "@/modules/module-manager/types";
import type { ModulePermission } from "@/context/AuthContext";
import { useSidebarExpandedSections } from "@/hooks/useSidebarState";
import { APP_VERSION } from "@/version";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SUPER_ADMIN_ROLE = "Super Admin";
// Pages governed by a separate mechanism (SuperAdminRoute / requireSuperAdmin),
// not by role_permissions rows — hidden from everyone except Super Admin.
const SUPER_ADMIN_ONLY_MODULES = new Set(["module_manager", "master_config"]);

// A few sensitive pages require a stronger grant than canView to be shown
// (preserves prior behaviour, e.g. API Keys was admin-gated).
const VISIBILITY_ACTION: Record<string, keyof ModulePermission> = {
  api_keys: "canAdmin",
};

type FallbackItem = { label: string; to: string; icon: LucideIcon };
type FallbackParent = {
  name: string;
  label: string;
  icon: LucideIcon;
  items: FallbackItem[];
};

const FALLBACK_DASHBOARD: FallbackItem = {
  label: "Dashboard",
  to: "/dashboard",
  icon: getIcon("LayoutDashboard"),
};

const FALLBACK_PARENTS: FallbackParent[] = [
  {
    name: "daily_work",
    label: "Daily Work",
    icon: getIcon("Briefcase"),
    items: [
      { label: "Meetings", to: "/meetings", icon: getIcon("CalendarDays") },
      { label: "Tasks", to: "/tasks", icon: getIcon("CheckSquare") },
      { label: "Actions", to: "/actions", icon: getIcon("Zap") },
      { label: "Notes", to: "/notes", icon: getIcon("FileText") },
      { label: "Milestones", to: "/milestones", icon: getIcon("Flag") },
    ],
  },
];

const parentItemBase =
  "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] text-[hsl(215,16%,30%)] transition-colors hover:bg-[hsl(210,50%,96%)]";
const parentItemActive =
  "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)] font-medium hover:bg-[hsl(210,55%,92%)]";
const parentActiveText = "text-[hsl(210,100%,28%)]";

const childItemBase =
  "mx-[6px] my-[1px] flex items-center gap-2 rounded-md text-[13px] text-[hsl(215,16%,47%)] transition-colors hover:bg-[hsl(210,50%,96%)]";
const childItemActive =
  "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)] font-medium hover:bg-[hsl(210,55%,92%)]";

function SkeletonItem() {
  return (
    <div className="flex items-center gap-2.5 px-3 py-1.5">
      <div className="h-[14px] w-[14px] animate-pulse rounded bg-[hsl(210,16%,90%)]" />
      <div className="h-[12px] w-24 animate-pulse rounded bg-[hsl(210,16%,90%)]" />
    </div>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: navModules, isLoading } = useNavModules();

  const isSuperAdmin = !!user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE);
  // Prefer cached modules over the hardcoded shell on transient errors —
  // initialData keeps `navModules` populated even after a 401/network failure.
  const useFallback = !navModules || navModules.length === 0;

  const [expandedSections, setExpandedSections] = useSidebarExpandedSections();

  type NavChild = {
    id: string;
    label: string;
    to: string;
    icon: LucideIcon;
    name: string;
  };
  type NavNode =
    | {
        kind: "leaf";
        id: string;
        name: string;
        label: string;
        icon: LucideIcon;
        to: string;
      }
    | {
        kind: "parent";
        id: string;
        name: string;
        label: string;
        icon: LucideIcon;
        children: NavChild[];
      };

  const navNodes = useMemo<NavNode[]>(() => {
    if (useFallback) {
      const nodes: NavNode[] = [
        {
          kind: "leaf",
          id: "fallback-dashboard",
          name: "dashboard",
          label: FALLBACK_DASHBOARD.label,
          icon: FALLBACK_DASHBOARD.icon,
          to: FALLBACK_DASHBOARD.to,
        },
        ...FALLBACK_PARENTS.map<NavNode>((p) => ({
          kind: "parent",
          id: p.name,
          name: p.name,
          label: p.label,
          icon: p.icon,
          children: p.items.map((i) => ({
            id: i.to,
            name: i.to,
            label: i.label,
            to: i.to,
            icon: i.icon,
          })),
        })),
      ];
      return nodes;
    }

    // Deny-by-default visibility, keyed off the registry's permissionKey.
    // A module is shown only when the role can view it; modules with no
    // permissionKey (dashboard/calendar/reports/attachments, parent headers)
    // are always visible.
    const perms = user?.permissions ?? {};
    const canSeeModule = (mod: {
      name: string;
      permissionKey: string | null;
    }): boolean => {
      if (isSuperAdmin) return true;
      if (SUPER_ADMIN_ONLY_MODULES.has(mod.name)) return false;
      if (!mod.permissionKey) return true;
      const action = VISIBILITY_ACTION[mod.permissionKey] ?? "canView";
      return !!perms[mod.permissionKey]?.[action];
    };

    const mods = navModules ?? [];
    const out: NavNode[] = [];
    for (const m of mods) {
      if (m.isParent) {
        const kids = m.children
          .filter((c: ModuleConfig) => canSeeModule(c))
          .map<NavChild>((c: ModuleConfig) => ({
            id: c.id,
            name: c.name,
            label: c.label,
            to: c.path,
            icon: getIcon(c.icon),
          }));
        // Hide an empty group entirely (e.g. CRM when the user lacks
        // enquiry view permission and it has no other children).
        if (kids.length === 0) continue;
        out.push({
          kind: "parent",
          id: m.id,
          name: m.name,
          label: m.label,
          icon: getIcon(m.icon),
          children: kids,
        });
      } else {
        if (!canSeeModule(m)) continue;
        out.push({
          kind: "leaf",
          id: m.id,
          name: m.name,
          label: m.label,
          icon: getIcon(m.icon),
          to: m.path,
        });
      }
    }
    return out;
  }, [navModules, useFallback, isSuperAdmin, user]);

  function isExpanded(name: string): boolean {
    return expandedSections[name] ?? false;
  }

  function toggleParent(name: string) {
    setExpandedSections((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  const showSkeleton = isLoading && !navModules;

  return (
    <aside
      className="flex h-screen w-[260px] shrink-0 flex-col border-r bg-white"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          borderBottom: "1px solid hsl(210,30%,90%)",
          height: "46px",
          width: "100%",
          boxSizing: "border-box",
          gap: "8px",
        }}
      >
        <img
          src="/phazex-logo.png"
          alt="PhazeX"
          style={{
            height: "32px",
            width: "auto",
            maxWidth: "140px",
            objectFit: "contain",
            display: "block",
          }}
        />
        <button
          type="button"
          onClick={() => navigate("/admin/changelog")}
          title="View changelog"
          className="ml-auto rounded-full bg-[hsl(210,55%,94%)] px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-[hsl(215,16%,40%)] transition-colors hover:bg-[hsl(210,55%,88%)] hover:text-[hsl(210,100%,28%)]"
        >
          v{APP_VERSION}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {showSkeleton ? (
          <div className="mt-3 space-y-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonItem key={i} />
            ))}
          </div>
        ) : (
          <div className="mt-3">
            {navNodes.map((node) => {
              if (node.kind === "leaf") {
                const Icon = node.icon ?? LayoutDashboard;
                return (
                  <div key={node.id} className="mb-1">
                    <NavLink
                      to={node.to}
                      className={({ isActive }) =>
                        cn(parentItemBase, isActive && parentItemActive)
                      }
                    >
                      <Icon
                        className="h-[14px] w-[14px] shrink-0"
                        strokeWidth={1.5}
                      />
                      <span className="flex-1 truncate text-left">
                        {node.label}
                      </span>
                    </NavLink>
                  </div>
                );
              }

              const open = isExpanded(node.name);
              const ParentIcon = node.icon ?? LayoutDashboard;
              const hasActiveChild = node.children.some(
                (c) => c.to === location.pathname,
              );
              return (
                <div key={node.id} className="mb-1">
                  <button
                    type="button"
                    onClick={() => toggleParent(node.name)}
                    className={cn(
                      parentItemBase,
                      "w-full",
                      hasActiveChild && !open && parentActiveText,
                      hasActiveChild && "font-medium",
                    )}
                  >
                    <ParentIcon
                      className="h-[14px] w-[14px] shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="flex-1 truncate text-left">
                      {node.label}
                    </span>
                    {open ? (
                      <ChevronDown
                        className="h-3.5 w-3.5 shrink-0"
                        strokeWidth={1.5}
                      />
                    ) : (
                      <ChevronRight
                        className="h-3.5 w-3.5 shrink-0"
                        strokeWidth={1.5}
                      />
                    )}
                  </button>
                  {open && node.children.length > 0 && (
                    <div className="mt-0.5">
                      {node.children.map((item) => {
                        const Icon = item.icon ?? LayoutDashboard;
                        return (
                          <NavLink
                            key={item.id}
                            to={item.to}
                            className={({ isActive }) =>
                              cn(childItemBase, isActive && childItemActive)
                            }
                            style={{ padding: "6px 12px 6px 36px" }}
                          >
                            <Icon
                              className="h-[14px] w-[14px] shrink-0"
                              strokeWidth={1.5}
                            />
                            <span className="truncate">{item.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {user && (
        <div
          className="border-t p-2"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus:outline-none focus-visible:bg-accent"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[11px] font-semibold text-[hsl(210,100%,28%)]">
                  {initials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-medium text-foreground">
                    {user.name}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {user.email}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              sideOffset={8}
              className="w-56"
            >
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Profile
              </DropdownMenuItem>
              {/* TODO: feedback page pending */}
              <DropdownMenuItem onClick={() => navigate("/feedback")}>
                <MessageSquarePlus
                  className="mr-2 h-4 w-4"
                  strokeWidth={1.5}
                />
                Send Feedback
              </DropdownMenuItem>
              {/* TODO: settings page pending */}
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Settings
              </DropdownMenuItem>
              {/* TODO: support page pending */}
              <DropdownMenuItem onClick={() => navigate("/support")}>
                <HelpCircle className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  void logout();
                }}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  );
}
